import { EventAggregator, ICustomElementViewModel, inject } from 'aurelia';

import { PageContentAreaService } from '../../../services/page-content-area-service';
import { SessionService } from '../../../services/session-service';
import { WebsiteService } from '../../../services/website-service';
import { websiteShortCode, environment } from '../../../environment';

import './footer.scss';
import { Helper } from '../../extensions';
import { SiteStyleService } from '../../../services/site-style-service';
import { IWebsitePage } from '@chicksgroup/types';
import { ProductCategoryService } from '../../../services/product-category-service';

@inject(PageContentAreaService, WebsiteService, SessionService, EventAggregator, Helper, SiteStyleService, ProductCategoryService)
export class Footer implements ICustomElementViewModel {
    detailsSelectors: { name: string, routeName: string };
    aboutUsRoute: { name: string, routeName: string };
    blogRoute: { name: string, routeName: string };
    contactUsRoute: { name: string, routeName: string };
    faqRoute: { name: string, routeName: string };
    sitemapRoute: { name: string, routeName: string };
    privacyPolicyRoute: { name: string, routeName: string };
    termsOfServiceRoute: { name: string, routeName: string };
    copyrightPolicyRoute: { name: string, routeName: string };
    bugBountyRoute: { name: string, routeName: string };
    locationRoute: { name: string, routeName: string };
    cookiesPolicyRoute: { name: string, routeName: string };
    viewingAsAdmin: boolean;
    adminViewSubscriber;
    trustPilotStarRating: string | number | null;
    amountOfStars: number;
    halfStar: string;
    semisesquiStar: string;
    websiteSection: { name: string, routeName: string }[] = [];
    supportSection: { name: string, routeName: string }[] = [];
    legalSection: { name: string, routeName: string }[] = [];
    shortCode: string = websiteShortCode() ?? 'CG';
    websiteShortCodes: string[] = [];
    selectedWebsiteShortcode: string;
    currentYear: number = new Date().getFullYear();
    environmentLink: string;
    footerLogo: string;
    legalSectionDropdown: { name: string, isDropdownMenu: boolean, showFilter: boolean, keyName: string, menuItems: { name: string, routeName: string }[]};
    websiteSectionDropdown: { name: string; routeName: string }[] = [];
    pages: IWebsitePage[] = [];
    buySellSwapDropdown: { name: string, isDropdownMenu?: boolean, showFilter?: boolean, keyName?: string, menuItems: { name: string, routeName: string }[]};
    env: string = environment() ?? 'local';
    footerIsLoaded: boolean = false;

    constructor(private pageContentAreaService: PageContentAreaService, private websiteService: WebsiteService, private sessionService: SessionService, private eventAggregator: EventAggregator, private helper: Helper, private siteStyleService: SiteStyleService) {
        this.websiteShortCodes = this.helper.websiteShortcodes;
        this.environmentLink = this.helper.environmentLink;
        this.footerLogo = this.helper.dynamicLogo;
    }

    get formattedWebsiteShortcodes() {
        return this.websiteShortCodes.map(code => {
            return {
                label: code,
                value: code
            };
        });
    }

    handleSelectedWebsiteShortcodeChange(e) {
        this.selectedWebsiteShortcode = e.detail.value;
        this.siteStyleService.loadSiteStyle(this.selectedWebsiteShortcode);
    }

    async attached() {
        this.pages = await this.websiteService.getPagesByWebsiteShortcode();
        this.websiteSection = [];
        this.supportSection = [];
        this.legalSection = [];
        this.legalSectionDropdown = await this.helper.websiteFooterLinks.legalSectionDropdownLinks;
        this.websiteSectionDropdown = await this.helper.websiteFooterLinks.websiteSectionDropdownLinks;

        this.locationRoute = this.pages.find(x => x.name === 'Locations' && x.routeName) ?? { name: 'Locations', routeName: 'locations' };
        this.contactUsRoute = this.pages.find(x => x.name === 'Contact Us' && x.routeName) ?? { name: 'Contact Us', routeName: 'contact' };
        this.aboutUsRoute = this.pages.find(x => x.name === 'About Us' && x.routeName) ?? { name: 'About Us', routeName: 'about-us' };
        this.blogRoute = this.pages.find(x => x.name === 'Blog' && x.routeName) ?? { name: 'Blog', routeName: '' };
        this.bugBountyRoute = this.pages.find(x => x.name === 'Bug Bounty' && x.routeName) ?? { name: 'Bug Bounty', routeName: 'bug-bounty' };
        this.sitemapRoute = this.pages.find(x => x.name === 'Sitemap' && x.routeName) ?? { name: 'Sitemap', routeName: 'sitemap' };
        this.faqRoute = this.pages.find(x => x.name === 'Faq' && x.routeName) ?? { name: 'FAQ', routeName: 'faq' };
        this.termsOfServiceRoute = this.pages.find(x => x.name === 'Terms Of Service' && x.routeName) ?? { name: 'Terms Of Service', routeName: 'terms-of-service' };
        this.privacyPolicyRoute = this.pages.find(x => x.name === 'Privacy Policy' && x.routeName) ?? { name: 'Privacy Policy', routeName: 'privacy-policy' };
        this.copyrightPolicyRoute = this.pages.find(x => x.name === 'Copyright Policy' && x.routeName) ?? { name: 'Copyright Policy', routeName: 'copyright-policy' };
        this.cookiesPolicyRoute = this.pages.find(x => x.name === 'Cookies Policy' && x.routeName) ?? { name: 'Cookies Policy', routeName: 'cookies-policy' };

        if (this.shortCode === 'CX') {
            this.websiteSection.push(this.locationRoute, this.blogRoute, this.bugBountyRoute);
            this.supportSection.push(this.contactUsRoute, this.faqRoute, this.sitemapRoute);
            this.legalSection.push(this.privacyPolicyRoute, this.termsOfServiceRoute, this.cookiesPolicyRoute);
        } else {
            this.websiteSection.push(this.aboutUsRoute, this.blogRoute, this.bugBountyRoute);
            this.supportSection.push(this.contactUsRoute, this.faqRoute, this.sitemapRoute);
            this.legalSection.push(this.privacyPolicyRoute, this.termsOfServiceRoute, this.copyrightPolicyRoute);
        }

        const homePage = this.pages.find(x => x.name === 'Home');
        if (homePage?.id !== undefined) {
            const pageContentArea = await this.pageContentAreaService.getByPageId(homePage.id);
            this.trustPilotStarRating = pageContentArea.find(x => x.key === 'HOME_TRUST_PILOT_RATING')?.markup ?? '4.8';
        }
        if (typeof this.trustPilotStarRating === 'string') {
            this.trustPilotStarRating = this.trustPilotStarRating.replace(/(<([^>]+)>)/gi, '').trim();
        }
        this.trustPilotStarRating = parseFloat(Number(this.trustPilotStarRating).toFixed(1));
        try {
            this.viewingAsAdmin = this.sessionService.getAdminView();
            this.handleEventSubscriptions();
        } catch (e) {
            console.log(e);
        }
        this.footerIsLoaded = true;
    }

    detached() {
        this.adminViewSubscriber?.dispose();
    }

    handleEventSubscriptions() {
        this.adminViewSubscriber = this.eventAggregator.subscribe('admin-view-updated', payload => {
            this.viewingAsAdmin = payload.bool;
        });
    }

    get footerWebLink() {
        switch (this.shortCode) {
            case 'CG':
                return 'ChicksGold.com';
            case 'CX':
                return 'ChicksX.com';
            case 'DS':
                return 'DivicaSales.com';
            case 'GT':
                return 'GamerTotal.com';
            case 'AG':
                return 'AribaGold.com';
            case 'AK':
                return 'AccKings.com';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get supportEmail() {
        switch (this.shortCode) {
            case 'DS':
                return 'support@divicasales.com';
            case 'CG':
                return 'support@chicksgold.com';
            case 'GT':
                return 'support@gamertotal.com';
            case 'CX':
                return 'support@chicksx.com';
            case 'AG':
                return 'support@aribagold.com';
            case 'AK':
                return 'support@acckings.com';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get trustpilotLink() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.trustpilot.com/review/chicksgold.com';
            case 'CX':
                return 'https://www.trustpilot.com/review/chicksx.com';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get trustpilotIncluded() {
        return this.shortCode === 'GT' || this.shortCode === 'DS' || this.shortCode === 'AG' || this.shortCode === 'AK' ? 'd-none' : '';
    }

    get socialMediaTwitter() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://twitter.com/chicksgold';
            case 'CX':
                return 'https://twitter.com/ChicksXcrypto';
            case 'DS':
                return 'https://twitter.com/SalesDivica';
            case 'GT':
                return 'https://twitter.com/GamerTotalcg';
            case 'AG':
                return 'https://twitter.com/aribagp';
            case 'AK':
                return 'https://twitter.com/Account_Kings';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get socialMediaFacebook() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.facebook.com/Chicksgroup';
            case 'CX':
                return 'https://www.facebook.com/chicksxcrypto';
            case 'DS':
                return 'https://www.facebook.com/Chicksgroup';
            case 'GT':
                return 'https://www.facebook.com/gamertotalcg';
            case 'AG':
                return 'https://www.facebook.com/aribagp';
            case 'AK':
                return 'https://www.facebook.com/acckingsofficial';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get socialMediaInstagram() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.instagram.com/chicksgold/';
            case 'CX':
                return 'https://www.instagram.com/chicksxcrypto/';
            case 'DS':
                return 'https://www.instagram.com/divicasales/';
            case 'GT':
                return 'https://www.instagram.com/gamertotalcg/';
            case 'AG':
                return 'https://www.instagram.com/aribagoldd/';
            case 'AK':
                return 'https://www.instagram.com/acc_kings/';
            default:
                return '';
        }
    }

    get socialMediaDiscord() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://discord.com/invite/chicksgroup';
            case 'CX':
                return 'https://discord.com/invite/chicksx-com';
            case 'DS':
                return 'https://discord.com/invite/chicksgroup';
            case 'GT':
                return 'https://discord.com/invite/chicksgroup';
            case 'AG':
                return 'https://discord.com/invite/chicksgroup';
            case 'AK':
                return 'https://discord.com/invite/chicksgroup';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get isLocal(): boolean {
        return this.env === 'local';
    }
}
